/**
 * @module modules/bisect-dialog-sk
 * @description <h2><code>bisect-dialog-sk</code></h2>
 *
 * Dialog to show when user wants to bisect with the corresponding plot data from chart-tooltip.
 * The bisect logic is only specific to Chrome.
 *
 * Takes the following inputs:
 * Request parameters:
 *   test path: the full string of trace name from chart-tooltip
 *   bug_id: Bug ID number, as a string (when submitting the form).
 *   start_revision: start revision time from input
 *   end_revision: end revision time from input
 *   story: the last sub_test name from test path
 * Once a validated user submits this dialog, there'll be an attempt to post a bisect
 * request. If succesful, user will see a pinpoint job URL. If unsuccesful, an
 * error message popup will appear.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "BisectDialogSk", {
    enumerable: true,
    get: function() {
        return BisectDialogSk;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
const _live = require("lit/directives/live.js");
const _task = require("@lit/task");
const _jsonOrThrow = require("../../../infra-sk/modules/jsonOrThrow");
const _errorMessage = require("../../../elements-sk/modules/errorMessage");
require("@material/web/icon/icon.js");
require("../../../elements-sk/modules/icons/close-icon-sk");
require("../../../elements-sk/modules/spinner-sk");
const _aloginsk = require("../../../infra-sk/modules/alogin-sk/alogin-sk");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
const STATISTIC_VALUES = [
    'avg',
    'count',
    'max',
    'min',
    'std',
    'sum'
];
let BisectDialogSk = class BisectDialogSk extends _lit.LitElement {
    createRenderRoot() {
        return this;
    }
    connectedCallback() {
        super.connectedCallback();
        (0, _aloginsk.LoggedIn)().then((status)=>{
            this.user = status.email;
        }).catch(_errorMessage.errorMessage);
    }
    setBisectInputParams(preloadBisectInputs) {
        this.testPath = preloadBisectInputs.testPath;
        this.startCommit = preloadBisectInputs.startCommit;
        this.endCommit = preloadBisectInputs.endCommit;
        this.bugId = preloadBisectInputs.bugId;
        this.story = preloadBisectInputs.story;
        this.anomalyId = preloadBisectInputs.anomalyId;
        this.patch = '';
    }
    open() {
        var _this__dialog;
        this._opened = true;
        this.patch = '';
        this._request = null;
        (_this__dialog = this._dialog) === null || _this__dialog === void 0 ? void 0 : _this__dialog.showModal();
    }
    closeBisectDialog() {
        var _this__dialog;
        this._opened = false;
        (_this__dialog = this._dialog) === null || _this__dialog === void 0 ? void 0 : _this__dialog.close();
    }
    get opened() {
        var _this__dialog;
        return (_this__dialog = this._dialog) === null || _this__dialog === void 0 ? void 0 : _this__dialog.open;
    }
    postBisect() {
        if (!this.opened) {
            return;
        }
        const parameters = this.testPath.split('/');
        const test = parameters.at(3) || '';
        const parts = test.split(':');
        // Pop up the last element from the array if exists
        const tail = parts.pop();
        let chart = test;
        let statistic = '';
        if (tail !== undefined) {
            chart = STATISTIC_VALUES.includes(tail) ? parts.join('_') : test;
            statistic = STATISTIC_VALUES.includes(tail) ? tail : '';
        }
        // Replace ':' with '_' for reduce errors when querying test paths in the legacy table
        // TODO(jiaxindong) b/431213645 follow up with the team about data backfill
        this.story = (parameters.pop() || '').replace(/:/g, '_');
        const req = {
            comparison_mode: 'performance',
            start_git_hash: this.startCommit,
            end_git_hash: this.endCommit,
            configuration: this.testPath.split('/')[1],
            benchmark: this.testPath.split('/')[2],
            story: this.story,
            chart: chart,
            statistic: statistic,
            comparison_magnitude: '',
            pin: this.patch,
            project: 'chromium',
            bug_id: this.bugId,
            user: this.user,
            alert_ids: '[' + this.anomalyId + ']',
            test_path: this.testPath,
            use_new_pinpoint: false
        };
        const validations = [
            {
                value: req.test_path,
                message: 'Test path is missing in the request.'
            },
            {
                value: req.start_git_hash,
                message: 'Start commit is required.'
            },
            {
                value: req.end_git_hash,
                message: 'End commit is required.'
            },
            {
                value: req.configuration,
                message: 'Configuration is missing in the request.'
            },
            {
                value: req.benchmark,
                message: 'Benchmark is missing in the request.'
            },
            {
                value: req.story,
                message: 'Story is missing in the request.'
            },
            {
                value: req.chart,
                message: 'Chart is missing in the request.'
            },
            {
                value: req.user,
                message: 'User is not logged in.'
            }
        ];
        for (const rule of validations){
            if (!rule.value) {
                (0, _errorMessage.errorMessage)(rule.message);
                return;
            }
        }
        this._request = req;
    }
    /** Clear Bisect Dialog fields */ reset() {
        this.startCommit = '';
        this.endCommit = '';
        this.story = '';
        this.testPath = '';
        this.anomalyId = '';
        this.patch = '';
        this._request = null;
    }
    render() {
        const isLoading = this._postBisectTask.status === _task.TaskStatus.PENDING;
        return (0, _lit.html)`
      <dialog id="bisect-dialog">
        <h2>Bisection</h2>
        <button id="bisectCloseIcon" @click=${this.closeBisectDialog}>
          <close-icon-sk></close-icon-sk>
        </button>
        <form id="bisect-form" @submit=${(e)=>e.preventDefault()}>
          <h3>Test Path</h3>
          <input
            id="testpath"
            type="text"
            .value=${(0, _live.live)(this.testPath)}
            @input=${(e)=>{
            this.testPath = e.target.value;
        }} />
          <h3>Bug ID</h3>
          <input
            id="bug-id"
            type="text"
            .value=${(0, _live.live)(this.bugId)}
            @input=${(e)=>{
            this.bugId = e.target.value;
        }} />
          <h3>Start Commit</h3>
          <input
            id="start-commit"
            type="text"
            .value=${(0, _live.live)(this.startCommit)}
            @input=${(e)=>{
            this.startCommit = e.target.value;
        }} />
          <h3>End Commit</h3>
          <input
            id="end-commit"
            type="text"
            .value=${(0, _live.live)(this.endCommit)}
            @input=${(e)=>{
            this.endCommit = e.target.value;
        }} />
          <h3>Story</h3>
          <input
            id="story"
            type="text"
            .value=${(0, _live.live)(this.story)}
            @input=${(e)=>{
            this.story = e.target.value;
        }} />
          <h3>Patch to apply to the entire job (optional)</h3>
          <input
            id="patch"
            type="text"
            .value=${(0, _live.live)(this.patch)}
            @input=${(e)=>{
            this.patch = e.target.value;
        }} />
          <div class="footer">
            <button
              id="submit-button"
              type="submit"
              @click=${this.postBisect}
              ?disabled=${isLoading}>
              Bisect
            </button>
            <spinner-sk id="dialog-spinner" ?active=${isLoading}></spinner-sk>
            ${this._renderTaskStatus()}
          </div>
        </form>
      </dialog>
    `;
    }
    _renderTaskStatus() {
        return this._postBisectTask.render({
            complete: (json)=>{
                if (!json) return (0, _lit.html)``;
                const url = json.jobUrl;
                if (!url) return (0, _lit.html)``;
                return (0, _lit.html)`<a id="pinpoint-job-url" href="${url}" target="_blank">
          Pinpoint Job Created<md-icon id="icon">open_in_new</md-icon>
        </a>`;
            },
            error: (e)=>{
                (0, _errorMessage.errorMessage)(e);
                return (0, _lit.html)``;
            }
        });
    }
    constructor(...args){
        super(...args);
        this.bugId = '';
        this.startCommit = '';
        this.endCommit = '';
        this.story = '';
        this.user = '';
        this.testPath = '';
        this.anomalyId = '';
        this.patch = '';
        this._opened = false;
        this._request = null;
        this._postBisectTask = new _task.Task(this, {
            task: async ([req], { signal })=>{
                if (!req) {
                    return null;
                }
                const response = await fetch('/_/bisect/create', {
                    method: 'POST',
                    signal,
                    body: JSON.stringify(req),
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });
                const json = await (0, _jsonOrThrow.jsonOrThrow)(response);
                return json;
            },
            args: ()=>[
                    this._request
                ]
        });
    }
};
_ts_decorate([
    (0, _decorators.property)({
        type: String
    }),
    _ts_metadata("design:type", String)
], BisectDialogSk.prototype, "bugId", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: String
    }),
    _ts_metadata("design:type", String)
], BisectDialogSk.prototype, "startCommit", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: String
    }),
    _ts_metadata("design:type", String)
], BisectDialogSk.prototype, "endCommit", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: String
    }),
    _ts_metadata("design:type", String)
], BisectDialogSk.prototype, "story", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", String)
], BisectDialogSk.prototype, "user", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: String
    }),
    _ts_metadata("design:type", String)
], BisectDialogSk.prototype, "testPath", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: String
    }),
    _ts_metadata("design:type", String)
], BisectDialogSk.prototype, "anomalyId", void 0);
_ts_decorate([
    (0, _decorators.query)('#bisect-dialog'),
    _ts_metadata("design:type", typeof HTMLDialogElement === "undefined" ? Object : HTMLDialogElement)
], BisectDialogSk.prototype, "_dialog", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", String)
], BisectDialogSk.prototype, "patch", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Boolean)
], BisectDialogSk.prototype, "_opened", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Object)
], BisectDialogSk.prototype, "_request", void 0);
BisectDialogSk = _ts_decorate([
    (0, _decorators.customElement)('bisect-dialog-sk')
], BisectDialogSk);
